import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  IconButton,
  HStack,
  VStack,
  useToast,
  Skeleton,
  Card,
  CardBody,
  useDisclosure,
  Tooltip,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, Building2, RefreshCw, ArrowLeft, Home, ChevronRight } from "lucide-react";
// IMPORT ACTIONS
import { getData, deleteData } from "../../redux/actions/common.action";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";

const AccountList = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const endpoint = "account";

  // Get data from Redux Common State
  const { accounts, loading } = useSelector((state: any) => state.common);

  const [search, setSearch] = useState("");
  const [deleteItem, setDeleteItem] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  // Fetch Data on Load (Using pagination limit 1000 to get all for client side filter)
  useEffect(() => {
    dispatch(getData(`${endpoint}?limit=1000`));
  }, [dispatch]);

  // Filter Data
  const displayData = useMemo(() => {
    const dataList = Array.isArray(accounts) ? accounts : []; // Safety check
    if (!search) return dataList;

    return dataList.filter(
      (item: any) =>
        item.bankName?.toLowerCase().includes(search.toLowerCase()) ||
        item.accountHolderName?.toLowerCase().includes(search.toLowerCase()) ||
        item.accountNo?.includes(search),
    );
  }, [accounts, search]);

  const handleDeleteClick = (item: any) => {
    setDeleteItem(item);
    onDeleteModalOpen();
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setIsDeleting(true);
    try {
      // Use existing Delete Action
      await dispatch(deleteData(deleteItem._id, endpoint, toast));
      // Refresh list
      dispatch(getData(`${endpoint}?limit=1000`));
      onDeleteModalClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Refresh Handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await dispatch(getData(`${endpoint}?limit=1000`));
      toast({
        title: "Refreshed",
        description: "Account list refreshed successfully",
        status: "success",
        position: "top-right",
        duration: 2000,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to refresh the list",
        status: "error",
        position: "top-right",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle Back Navigation
  const handleBack = () => {
    navigate("/admission-list");
  };

  return (
    <Box p={{ base: 2, md: 6 }} bg="gray.50" minH="100vh">
      {/* Header Section with Back Button and Breadcrumb */}
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        mb={6}
        direction={{ base: "column", md: "row" }}
        gap={4}
      >
        <VStack align="start" spacing={2}>
          <Button
            leftIcon={<ArrowLeft size={16} />}
            onClick={handleBack}
            size="sm"
            variant="ghost"
            colorScheme="blue"
            fontWeight="medium"
            fontSize="sm"
            _hover={{
              bg: "blue.50",
              transform: "translateX(-4px)",
            }}
            transition="all 0.2s"
          >
            Back
          </Button>

          <Breadcrumb
            spacing={2}
            separator={<ChevronRight size={12} color="#9CA3AF" />}
            fontSize="xs"
            color="gray.600"
            display={{ base: "none", md: "flex" }}
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={handleBack}
                display="flex"
                alignItems="center"
                gap={1}
                _hover={{ color: "blue.500" }}
                cursor="pointer"
              >
                <Home size={12} />
                <Text>Admissions</Text>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink
                display="flex"
                alignItems="center"
                gap={1}
                color="blue.500"
                fontWeight="medium"
              >
                <Building2 size={12} />
                <Text>Bank Accounts</Text>
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
        </VStack>

        <Flex gap={3} w={{ base: "full", md: "auto" }} flexWrap="wrap">
          <InputGroup
            w={{ base: "full", md: "300px" }}
            bg="white"
            borderRadius="md"
          >
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              type="text"
              placeholder="Search Bank, Holder or Acc No..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              border="1px solid"
              borderColor="gray.200"
              _focus={{ borderColor: "blue.400", boxShadow: "none" }}
            />
          </InputGroup>

          <Tooltip label="Refresh list" placement="top">
            <IconButton
              aria-label="Refresh"
              icon={<RefreshCw size={18} />}
              colorScheme="gray"
              variant="outline"
              onClick={handleRefresh}
              isLoading={isRefreshing}
              transition="transform 0.2s"
              _hover={{
                transform: "rotate(180deg)",
              }}
            />
          </Tooltip>

          <Button
            leftIcon={<Plus size={18} />}
            colorScheme="blue"
            onClick={() => navigate("/account-form")}
            px={6}
          >
            Add Account
          </Button>
        </Flex>
      </Flex>

      {/* Table Section */}
      <Card
        variant="outline"
        boxShadow="sm"
        borderRadius="xl"
        overflow="hidden"
      >
        <CardBody p={0}>
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th py={4}>#</Th>
                  <Th py={4}>Account Holder</Th>
                  <Th py={4}>Bank Info</Th>
                  <Th py={4}>Account No</Th>
                  <Th py={4}>IFSC</Th>
                  <Th py={4}>UPI ID</Th>
                  <Th py={4} textAlign="center">
                    Date Added
                  </Th>
                  <Th py={4} textAlign="right" pr={6}>
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <Tr key={i}>
                      {[...Array(8)].map((_, j) => (
                        <Td key={j}>
                          <Skeleton height="20px" />
                        </Td>
                      ))}
                    </Tr>
                  ))
                ) : displayData.length > 0 ? (
                  displayData.map((acc: any, index: number) => (
                    <Tr
                      key={acc._id}
                      _hover={{ bg: "gray.50" }}
                      transition="0.2s"
                    >
                      <Td color="gray.500" fontSize="xs">
                        {index + 1}
                      </Td>
                      <Td>
                        <Text fontWeight="medium" color="gray.700">
                          {acc.accountHolderName}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {acc.mobileNo}
                        </Text>
                      </Td>
                      <Td>
                        <Badge colorScheme="blue" variant="subtle" mb={1}>
                          {acc.bankName}
                        </Badge>
                        <Text fontSize="xs" color="gray.500">
                          {acc.branch}
                        </Text>
                      </Td>
                      <Td fontFamily="mono" color="gray.600">
                        {acc.accountNo}
                      </Td>
                      <Td fontFamily="mono" fontSize="xs" color="gray.500">
                        {acc.IFSCCode}
                      </Td>
                      <Td>
                        {acc.upiId ? (
                          <Text fontSize="sm" fontWeight="500">
                            {acc.upiId}
                          </Text>
                        ) : (
                          <Text fontSize="sm" color="gray.400">
                            -
                          </Text>
                        )}
                      </Td>
                      <Td textAlign="center" fontSize="xs" color="gray.500">
                        {new Date(acc.createdAt).toLocaleDateString()}
                      </Td>
                      <Td textAlign="right" pr={4}>
                        <HStack justify="flex-end">
                          <IconButton
                            aria-label="Edit"
                            icon={<Edit2 size={16} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() =>
                              navigate("/account-form", { state: acc })
                            }
                          />
                          <IconButton
                            aria-label="Delete"
                            icon={<Trash2 size={16} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDeleteClick(acc)}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={8} textAlign="center" py={10}>
                      <Text color="gray.500">No accounts found.</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
      </Card>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        onConfirm={confirmDelete}
        title="Delete Account"
        itemName={deleteItem?.bankName}
        itemType="Account"
        description="Are you sure you want to delete this bank account details?"
      />
    </Box>
  );
};

export default AccountList;
